import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import FullCalendar from '@fullcalendar/react';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import nbLocale from '@fullcalendar/core/locales/nb';

import {
  fetchAllEmployeeResources,
  fetchAllProductionJobs,
  createProductionJob,
  updateProductionJob,
  deleteProductionJob,
  addOperatorsToProductionJob,
  deleteOperatorFromJob,
  revertProductionJob,
  fetchResourceWorkload,
} from '../services/productionPlanService';
import { searchWorkOrders } from '../services/workOrderService';
import { getApiUrl } from '../utils/apiUrl';
import './FormStyles.css';
import './AlleAnsatteStyles.css';

// Format a Date as YYYY-MM-DD using local timezone (avoids toISOString UTC shift)
const toLocalDateString = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};


// Remove any lingering event tooltips from the DOM. Tooltips are appended to
// document.body on hover; if the calendar re-renders (loadData/socket) while
// hovering, the event node is replaced and its mouseleave never fires, leaving
// the tooltip orphaned on screen. This clears all of them defensively.
function removeAllEventTooltips() {
  document.querySelectorAll('.event-tooltip').forEach((el) => el.remove());
}

// Norwegian public holidays (helligdager) for a given year
function getNorwegianHolidays(year) {
  // Easter calculation (Anonymous Gregorian algorithm)
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  const easter = new Date(year, month - 1, day);

  const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  return [
    { date: fmt(new Date(year, 0, 1)), name: 'Nyttårsdag' },
    { date: fmt(addDays(easter, -3)), name: 'Skjærtorsdag' },
    { date: fmt(addDays(easter, -2)), name: 'Langfredag' },
    { date: fmt(easter), name: '1. påskedag' },
    { date: fmt(addDays(easter, 1)), name: '2. påskedag' },
    { date: fmt(new Date(year, 4, 1)), name: 'Arbeidernes dag' },
    { date: fmt(new Date(year, 4, 17)), name: 'Grunnlovsdag' },
    { date: fmt(addDays(easter, 39)), name: 'Kristi himmelfartsdag' },
    { date: fmt(addDays(easter, 49)), name: '1. pinsedag' },
    { date: fmt(addDays(easter, 50)), name: '2. pinsedag' },
    { date: fmt(new Date(year, 11, 25)), name: '1. juledag' },
    { date: fmt(new Date(year, 11, 26)), name: '2. juledag' },
  ];
}

const AlleAnsatteWidget = ({ token, currentUser, onClose, onOpenInOrdersFlow }) => {
  const [resources, setResources] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const calendarRef = useRef();
  const currentDateRef = useRef(new Date());
  const [createModal, setCreateModal] = useState(null); // { resourceId, resourceName, start, end }
  const [editModal, setEditModal] = useState(null); // { jobId, resourceId, resourceName, start, end, jobType, ordreNr, customer, memo, completed }
  const [highlightedResource, setHighlightedResource] = useState(null); // resourceId of highlighted row
  const [currentViewType, setCurrentViewType] = useState('resourceTimelineMonth');
  const [showMyTeamOnly, setShowMyTeamOnly] = useState(false);
  const [workerSearch, setWorkerSearch] = useState('');
  const [editSiblings, setEditSiblings] = useState([]); // array of sibling job data for overlap picker

  // Work order lookup for the create modal
  const [orderSearch, setOrderSearch] = useState('');
  const [orderResults, setOrderResults] = useState([]);
  const [orderSearchLoading, setOrderSearchLoading] = useState(false);

  // Absence sidebar
  const [showAbsenceSidebar, setShowAbsenceSidebar] = useState(false);
  const [pendingAbsences, setPendingAbsences] = useState([]);
  const [absenceLoading, setAbsenceLoading] = useState(false);

  // Resource workload (edit modal)
  const [editWorkload, setEditWorkload] = useState(null);

  // Custom alert modal (replaces native showAlert())
  const [systemAlert, setSystemAlert] = useState(null); // { message: string }
  const showAlert = (msg) => setSystemAlert({ message: msg });

  // Audit log modal
  const [showLogModal, setShowLogModal] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [auditLogError, setAuditLogError] = useState('');

  // Split a multi-day absence event into individual weekday segments (07:00-15:10 each)
  const splitAbsenceIntoDays = (startStr, endStr) => {
    if (!startStr || !endStr) return null;
    const startDate = new Date(startStr);
    const endDate = new Date(endStr);
    if (startDate.toDateString() === endDate.toDateString()) return null;
    const days = [];
    const current = new Date(startDate);
    current.setHours(0, 0, 0, 0);
    const lastDay = new Date(endDate);
    lastDay.setHours(0, 0, 0, 0);
    while (current <= lastDay) {
      const dow = current.getDay();
      if (dow !== 0 && dow !== 6) {
        // Use local date formatting to avoid timezone shift (toISOString uses UTC)
        const y = current.getFullYear();
        const m = String(current.getMonth() + 1).padStart(2, '0');
        const d = String(current.getDate()).padStart(2, '0');
        const dateStr = `${y}-${m}-${d}`;
        days.push({ start: `${dateStr}T07:00:00`, end: `${dateStr}T15:10:00` });
      }
      current.setDate(current.getDate() + 1);
    }
    return days.length > 0 ? days : null;
  };

  const mapJobsToEvents = (jobs) => {
    const mappedEvents = [];
    jobs.forEach(j => {
      const jobType = j.job_type || 'normal';
      let bgColor, borderColor, titlePrefix;

      if (j.completed) {
        bgColor = "#16a34a";
        borderColor = "#16a34a";
        titlePrefix = "✓ ";
      } else {
        switch (jobType) {
          case 'syk':
            bgColor = "#ef4444"; borderColor = "#dc2626"; titlePrefix = "🤒 "; break;
          case 'ferie':
            bgColor = "#f59e0b"; borderColor = "#d97706"; titlePrefix = "🏖️ "; break;
          case 'permission_paid':
          case 'permission_unpaid':
            bgColor = "#3b82f6"; borderColor = "#4f46e5"; titlePrefix = ""; break;
          case 'reserve':
            bgColor = "#a78bfa"; borderColor = "#7c3aed"; titlePrefix = "📌 "; break;
          default:
            bgColor = "#14b8a6"; borderColor = "#0d9488"; titlePrefix = "";
        }
      }

      // Detect overtime: memo contains "overtid" OR job ends after 15:10 on a weekday
      const _memoLower = (j.memo || '').toLowerCase();
      const _isOvertime = _memoLower.includes('overtid') || (() => {
        const endDt = j.end_time ? new Date(j.end_time) : null;
        if (!endDt) return false;
        const day = endDt.getDay();
        if (day === 0 || day === 6) return false;
        const endMinutes = endDt.getHours() * 60 + endDt.getMinutes();
        return endMinutes > 15 * 60 + 10;
      })();
      if (_isOvertime && !j.completed) {
        bgColor = "#dc2626"; borderColor = "#991b1b"; titlePrefix = "⏰ ";
      }

      if (j.operators && Array.isArray(j.operators) && j.operators.length > 0) {
        const allOperators = j.operators.map(op => ({ resourceId: op.resourceId, start: op.start, end: op.end }));
        const peoplePerDay = {};
        j.operators.forEach(op => {
          const dayKey = op.start ? new Date(op.start).toDateString() : '';
          if (!peoplePerDay[dayKey]) peoplePerDay[dayKey] = new Set();
          peoplePerDay[dayKey].add(op.resourceId);
        });
        j.operators.forEach((op, index) => {
          const dayKey = op.start ? new Date(op.start).toDateString() : '';
          const peopleThisDay = peoplePerDay[dayKey] ? peoplePerDay[dayKey].size : 1;

          // Split multi-day entries into daily segments (07:00-15:10 per weekday)
          const segments = splitAbsenceIntoDays(op.start, op.end);
          if (segments) {
            segments.forEach((seg, si) => {
              mappedEvents.push({
                id: `${j.id}_op${index}_d${si}`,
                resourceId: op.resourceId,
                title: titlePrefix + (j.order_number || j.customer || "Jobb"),
                start: seg.start, end: seg.end,
                backgroundColor: bgColor, borderColor: borderColor,
                extendedProps: {
                  ordreNr: j.order_number, customer: j.customer, memo: j.memo,
                  jobType, createdBy: j.created_by_name || j.created_by_email || null, createdAt: j.created_at || null,
                  completed: !!j.completed, completedBy: j.completed_by_name || j.completed_by_email || null, completedAt: j.completed_at || null,
                  isMultiOperator: true, operatorIndex: index + 1, totalOperators: peopleThisDay,
                  allOperators, parentJobId: j.id, workOrderId: j.work_order_id || null, teamId: j.team_id || null,
                  eventResourceId: op.resourceId
                }
              });
            });
            return;
          }

          mappedEvents.push({
            id: `${j.id}_op${index}`,
            resourceId: op.resourceId,
            title: titlePrefix + (j.order_number || j.customer || "Jobb"),
            start: op.start, end: op.end,
            backgroundColor: bgColor, borderColor: borderColor,
            extendedProps: {
              ordreNr: j.order_number, customer: j.customer, memo: j.memo,
              jobType, createdBy: j.created_by_name || j.created_by_email || null, createdAt: j.created_at || null,
              completed: !!j.completed, completedBy: j.completed_by_name || j.completed_by_email || null, completedAt: j.completed_at || null,
              isMultiOperator: true, operatorIndex: index + 1, totalOperators: peopleThisDay,
              allOperators, parentJobId: j.id, workOrderId: j.work_order_id || null, teamId: j.team_id || null,
              eventResourceId: op.resourceId
            }
          });
        });
      } else {
        // Split multi-day entries into daily segments (07:00-15:10 per weekday)
        const segments = splitAbsenceIntoDays(j.start_time, j.end_time);
        if (segments) {
          segments.forEach((seg, si) => {
            mappedEvents.push({
              id: `${j.id}_d${si}`,
              resourceId: String(j.resource_id || j.machine_id),
              title: titlePrefix + (j.order_number || j.customer || "Jobb"),
              start: seg.start, end: seg.end,
              backgroundColor: bgColor, borderColor: borderColor,
              extendedProps: {
                ordreNr: j.order_number, customer: j.customer, memo: j.memo,
                jobType, createdBy: j.created_by_name || j.created_by_email || null, createdAt: j.created_at || null,
                completed: !!j.completed, completedBy: j.completed_by_name || j.completed_by_email || null, completedAt: j.completed_at || null,
                isMultiOperator: false, workOrderId: j.work_order_id || null, teamId: j.team_id || null,
                eventResourceId: String(j.resource_id || j.machine_id), parentJobId: j.id
              }
            });
          });
          return;
        }

        mappedEvents.push({
          id: String(j.id),
          resourceId: String(j.resource_id || j.machine_id),
          title: titlePrefix + (j.order_number || j.customer || "Jobb"),
          start: j.start_time || "", end: j.end_time || "",
          backgroundColor: bgColor, borderColor: borderColor,
          extendedProps: {
            ordreNr: j.order_number, customer: j.customer, memo: j.memo,
            jobType, createdBy: j.created_by_name || j.created_by_email || null, createdAt: j.created_at || null,
            completed: !!j.completed, completedBy: j.completed_by_name || j.completed_by_email || null, completedAt: j.completed_at || null,
            isMultiOperator: false, workOrderId: j.work_order_id || null, teamId: j.team_id || null,
            eventResourceId: String(j.resource_id || j.machine_id)
          }
        });
      }
    });
    return mappedEvents;
  };

  const loadData = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError('');
      const [resData, jobsData] = await Promise.all([
        fetchAllEmployeeResources(token),
        fetchAllProductionJobs(token)
      ]);
      const mapped = resData
        .filter(r => !(r.title || '').toUpperCase().includes('MAIN ADMIN'))
        .map((r, idx) => ({
          id: String(r.id),
          title: r.title || `Ansatt ${r.id}`,
          teamName: r.teamName || 'Uten team',
          resourceType: 'employee',
          order: idx
        }));
      setResources(mapped);
      setEvents(mapJobsToEvents(jobsData));
    } catch (err) {
      console.error('[AlleAnsatte] Error loading data:', err);
      setError('Kunne ikke laste data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fetch pending absence requests
  const loadPendingAbsences = useCallback(async () => {
    if (!token) return;
    try {
      setAbsenceLoading(true);
      const res = await fetch(`${getApiUrl()}/absence-requests/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingAbsences(data);
      }
    } catch (err) {
      console.error('[AlleAnsatte] Error loading pending absences:', err);
    } finally {
      setAbsenceLoading(false);
    }
  }, [token]);

  // Load pending count on mount, and full data when sidebar opens
  useEffect(() => {
    loadPendingAbsences();
  }, [loadPendingAbsences]);

  useEffect(() => {
    if (showAbsenceSidebar) loadPendingAbsences();
  }, [showAbsenceSidebar, loadPendingAbsences]);

  const handleAbsenceAction = async (id, action) => {
    try {
      const res = await fetch(`${getApiUrl()}/absence-requests/${id}/${action}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Feil');
      await loadPendingAbsences();
    } catch (err) {
      showAlert('Kunne ikke behandle forespørsel: ' + err.message);
    }
  };

  const toggleAbsenceSidebar = () => setShowAbsenceSidebar(prev => !prev);

  // Fetch calendar audit log (create/update/delete history)
  const loadAuditLogs = useCallback(async () => {
    if (!token) return;
    try {
      setAuditLogLoading(true);
      setAuditLogError('');
      const res = await fetch(`${getApiUrl()}/production-plan/jobs/audit-log?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Kunne ikke hente logg');
      }
      const data = await res.json();
      setAuditLogs(data.logs || []);
    } catch (err) {
      setAuditLogError(err.message);
    } finally {
      setAuditLogLoading(false);
    }
  }, [token]);

  const openLogModal = () => {
    setShowLogModal(true);
    loadAuditLogs();
  };

  // Revert handler — undoes a calendar change using audit log data
  const [reverting, setReverting] = useState(false);

  const handleRevert = async (auditLogId, summary) => {
    if (!window.confirm(`Tilbakestille denne endringen?\n\n${summary || ''}`)) return;
    try {
      setReverting(true);
      await revertProductionJob(auditLogId, token);
      await loadData();
      if (showLogModal) await loadAuditLogs();
    } catch (err) {
      showAlert('Kunne ikke tilbakestille: ' + err.message);
    } finally {
      setReverting(false);
    }
  };

  const canCreate = currentUser?.isAdmin || currentUser?.isTeamLeader || currentUser?.canCreateWorkOrder || currentUser?.canPlanAllTeams || currentUser?.canPlanProduction;
  const canReserveDates = currentUser?.isAdmin || currentUser?.canReserveDates;

  // Quick undo last action
  const [canUndoLast, setCanUndoLast] = useState(false);

  const checkCanUndoLast = useCallback(async () => {
    if (!token || !canCreate) return;
    try {
      const res = await fetch(`${getApiUrl()}/production-plan/jobs/audit-log?limit=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.logs && data.logs.length > 0) {
        const lastLog = data.logs[0];
        setCanUndoLast(currentUser?.isAdmin || lastLog.user_id === currentUser?.id);
      } else {
        setCanUndoLast(false);
      }
    } catch {}
  }, [token, canCreate, currentUser]);

  useEffect(() => {
    checkCanUndoLast();
  }, [checkCanUndoLast]);

  const handleUndoLast = async () => {
    try {
      setReverting(true);
      const res = await fetch(`${getApiUrl()}/production-plan/jobs/audit-log?limit=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Kunne ikke hente logg');
      const data = await res.json();
      if (!data.logs || data.logs.length === 0) {
        showAlert('Ingen endringer å tilbakestille');
        return;
      }
      const lastLog = data.logs[0];
      if (!currentUser?.isAdmin && lastLog.user_id !== currentUser?.id) {
        showAlert('Du kan bare tilbakestille egne endringer');
        return;
      }
      await handleRevert(lastLog.id, lastLog.changes_summary);
      await checkCanUndoLast();
    } catch (err) {
      showAlert('Kunne ikke tilbakestille: ' + err.message);
    } finally {
      setReverting(false);
    }
  };

  // Socket.IO real-time updates
  useEffect(() => {
    if (!token) return;
    const apiUrl = getApiUrl().replace('/api', '');
    const socket = io(apiUrl, { transports: ['websocket', 'polling'], auth: { token } });

    socket.on('connect', () => {
      console.log('[AlleAnsatte] Socket connected - live updates enabled');
    });

    socket.on('productionJobsUpdated', () => {
      console.log('[AlleAnsatte] Jobs updated via socket, refreshing...');
      loadData();
      loadPendingAbsences();
      checkCanUndoLast();
    });

    return () => { socket.disconnect(); };
  }, [token, loadData, loadPendingAbsences, checkCanUndoLast]);

  // Memoize holidays so they're not recalculated on every render
  const holidayMap = useMemo(() => {
    const now = new Date();
    const all = [
      ...getNorwegianHolidays(now.getFullYear() - 1),
      ...getNorwegianHolidays(now.getFullYear()),
      ...getNorwegianHolidays(now.getFullYear() + 1),
    ];
    const map = {};
    all.forEach(h => { map[h.date] = h.name; });
    return map;
  }, []);

  // Memoize holiday background events
  const holidayEvents = useMemo(() => {
    return Object.entries(holidayMap).map(([date, name]) => ({
      id: `holiday-${date}`,
      title: name,
      start: date,
      end: date,
      display: 'background',
      backgroundColor: 'rgba(239, 68, 68, 0.15)',
      borderColor: 'transparent',
      allDay: true,
      extendedProps: { isHoliday: true, holidayName: name }
    }));
  }, [holidayMap]);

  // Memoize combined events
  const allEvents = useMemo(() => [...events, ...holidayEvents], [events, holidayEvents]);

  // Pre-compute overlap map: key = "resourceId|dateStr" -> { count, hasOverlap }
  const overlapMap = useMemo(() => {
    const dayMap = {}; // key -> array of { start, end }
    events.forEach(ev => {
      if (ev.extendedProps?.isHoliday) return;
      const resId = ev.extendedProps?.eventResourceId || ev.resourceId;
      const dateStr = ev.start ? new Date(ev.start).toDateString() : null;
      if (!resId || !dateStr) return;
      const key = `${resId}|${dateStr}`;
      if (!dayMap[key]) dayMap[key] = [];
      dayMap[key].push({
        id: ev.id,
        start: ev.start ? new Date(ev.start).getTime() : 0,
        end: ev.end ? new Date(ev.end).getTime() : 0,
      });
    });
    const result = {};
    Object.entries(dayMap).forEach(([key, jobs]) => {
      let hasOverlap = false;
      if (jobs.length > 1) {
        for (let i = 0; i < jobs.length && !hasOverlap; i++) {
          for (let j = i + 1; j < jobs.length && !hasOverlap; j++) {
            if (jobs[i].start < jobs[j].end && jobs[j].start < jobs[i].end) hasOverlap = true;
          }
        }
      }
      // Store index + sibling IDs for each event
      const siblingIds = jobs.map(j => j.id);
      jobs.forEach((job, idx) => {
        result[job.id] = { index: idx + 1, total: jobs.length, hasOverlap, siblingIds };
      });
    });
    return result;
  }, [events]);

  // IDs of resources that share a team with the current user (for day-view highlight)
  const myTeamResourceIds = useMemo(() => {
    if (!currentUser || resources.length === 0) return new Set();
    let myRes = null;
    if (currentUser.employeeId) {
      myRes = resources.find(r => r.id === `employee_${currentUser.employeeId}`);
    }
    if (!myRes) {
      const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ');
      if (fullName) {
        myRes = resources.find(r => r.title && r.title.toLowerCase() === fullName.toLowerCase());
      }
    }
    if (!myRes || !myRes.teamName || myRes.teamName === 'Uten team') return new Set();
    const myTeamName = myRes.teamName.trim().toLowerCase();
    const ids = new Set();
    resources.forEach(r => {
      if (!r.teamName || r.teamName === 'Uten team') return;
      if (r.teamName.trim().toLowerCase() === myTeamName) ids.add(r.id);
    });
    return ids;
  }, [resources, currentUser]);

  // Narrow visible rows to employees matching the search term — either by their
  // own name, or by having a job whose order/customer/memo matches.
  const filteredResources = useMemo(() => {
    let base = resources;
    // Filter to team members only when "Mitt team" is active
    if (showMyTeamOnly && myTeamResourceIds.size > 0) {
      base = resources.filter(r => myTeamResourceIds.has(r.id));
    }
    const term = searchTerm.trim().toLowerCase();
    if (!term) return base;
    const matchedResourceIds = new Set();
    events.forEach(ev => {
      const ep = ev.extendedProps || {};
      if (ep.isHoliday) return;
      const hit = (ep.ordreNr || '').toLowerCase().includes(term)
        || (ep.customer || '').toLowerCase().includes(term)
        || (ep.memo || '').toLowerCase().includes(term);
      if (hit) matchedResourceIds.add(String(ep.eventResourceId || ev.resourceId));
    });
    return base.filter(r =>
      matchedResourceIds.has(String(r.id)) || (r.title || '').toLowerCase().includes(term)
    );
  }, [resources, events, searchTerm, showMyTeamOnly, myTeamResourceIds]);

  // Handle calendar slot selection — open job type picker
  const handleSelectSlot = (info) => {
    if (!canCreate) return;
    const resource = info.resource;
    if (!resource) return;
    const resourceName = resource.title || 'Ansatt';
    const resourceId = resource.id; // e.g. "employee_123" or just "123"
    // Extract date parts (startStr can be datetime in day view)
    const startDateStr = (info.startStr || '').split('T')[0];
    // FullCalendar end date is exclusive — subtract 1 day for display
    const endDate = new Date(info.endStr);
    endDate.setDate(endDate.getDate() - 1);
    // Use local date formatting to avoid timezone shift
    const ey = endDate.getFullYear();
    const em = String(endDate.getMonth() + 1).padStart(2, '0');
    const ed = String(endDate.getDate()).padStart(2, '0');
    const adjustedEnd = `${ey}-${em}-${ed}`;
    // If end is before start (same-day selection in day view), use same date
    const finalEnd = adjustedEnd < startDateStr ? startDateStr : adjustedEnd;
    setCreateModal({
      resourceId,
      resourceName,
      start: startDateStr,
      end: finalEnd,
    });
  };

  // Step 1: select job type → go to step 2
  const handleSelectJobType = (jobType) => {
    setWorkerSearch('');
    setOrderSearch('');
    setOrderResults([]);
    // If the selected date is today, use current time as start for single-day events (no back-registering).
    // For multi-day absences, always use 07:00 so the whole first day is covered.
    const todayStr = toLocalDateString(new Date());
    const selectedDate = createModal?.start?.split('T')[0] || todayStr;
    const isToday = selectedDate === todayStr;
    const isPartial = createModal?.partialDay;
    const now = new Date();
    const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    setCreateModal(prev => ({
      ...prev, step: 2, jobType, ordreNr: '', customer: '', memo: '', coWorkers: [],
      startTime: (isToday && isPartial) ? currentTimeStr : '07:00', endTime: '15:10', workOrderId: null, linkedOrder: null,
      partialDay: false,
    }));
  };

  // Debounced work order lookup — only while the create modal is on step 2
  const createStep = createModal?.step;
  useEffect(() => {
    const term = orderSearch.trim();
    if (createStep !== 2 || term.length < 2) {
      setOrderResults([]);
      setOrderSearchLoading(false);
      return;
    }
    let cancelled = false;
    setOrderSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const orders = await searchWorkOrders(term, token, 10);
        if (!cancelled) setOrderResults(orders);
      } catch (err) {
        if (!cancelled) setOrderResults([]);
      } finally {
        if (!cancelled) setOrderSearchLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [orderSearch, createStep, token]);

  // Link a work order to the job being created — fills in order number and customer
  const handleSelectWorkOrder = (order) => {
    setCreateModal(prev => ({
      ...prev,
      workOrderId: order.id,
      linkedOrder: order,
      ordreNr: order.orderNumber || prev.ordreNr || '',
      customer: order.customer || prev.customer || '',
    }));
    setOrderSearch('');
    setOrderResults([]);
  };

  // Unlink — keeps whatever text is already in the order number / customer fields
  const handleUnlinkWorkOrder = () => {
    setCreateModal(prev => ({ ...prev, workOrderId: null, linkedOrder: null, assignTeamId: null }));
  };

  // Ordrenummer, Kunde and Notat are mandatory for normal jobs.
  // Absence types (ferie/syk/permission) don't show those fields at all.
  const isCreateFormValid = (m) => {
    if (!m || m.jobType !== 'normal') return true;
    return Boolean((m.ordreNr || '').trim() && (m.customer || '').trim() && (m.memo || '').trim());
  };

  // Step 2: submit the form
  const handleCreateJob = async () => {
    if (!createModal) return;
    if (!isCreateFormValid(createModal)) {
      const missing = [];
      if (!(createModal.ordreNr || '').trim()) missing.push('Ordrenummer');
      if (!(createModal.customer || '').trim()) missing.push('Kunde');
      if (!(createModal.memo || '').trim()) missing.push('Notat');
      showAlert(`Følgende felt må fylles ut: ${missing.join(', ')}`);
      return;
    }
    try {
      const startDate = createModal.start?.split('T')[0] || createModal.start;
      const endDate = createModal.end?.split('T')[0] || createModal.end;
      const startTime = createModal.startTime || '07:00';
      const endTime = createModal.endTime || '15:10';
      const jobType = createModal.jobType;
      const isAbsenceType = ['syk', 'ferie', 'permission_paid', 'reserve'].includes(jobType);
      const partialDay = isAbsenceType && createModal.partialDay;

      // Prevent back-registering: don't allow start time in the past for normal jobs
      // Allow a 5-minute grace period so the user isn't blocked when selecting "now"
      if (jobType === 'normal') {
        const todayStr = toLocalDateString(new Date());
        if (startDate === todayStr) {
          const now = new Date();
          const graceMs = 5 * 60 * 1000; // 5 minutes
          const cutoff = new Date(now.getTime() - graceMs);
          const cutoffStr = `${String(cutoff.getHours()).padStart(2, '0')}:${String(cutoff.getMinutes()).padStart(2, '0')}`;
          if (startTime < cutoffStr) {
            showAlert('Du kan ikke planlegge en jobb med starttid i fortiden. Starttid satt til nå.');
            const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
            setCreateModal(prev => ({ ...prev, startTime: currentTimeStr }));
            return;
          }
        }
      }

      // Partial-day absence: single day with custom start/end times
      if (partialDay) {
        const fullStart = `${startDate}T${startTime}:00`;
        const fullEnd = `${startDate}T${endTime}:00`;
        const result = await createProductionJob({
          resourceId: createModal.resourceId,
          start: fullStart,
          end: fullEnd,
          jobType,
          ordreNr: createModal.ordreNr || '',
          customer: createModal.customer || '',
          memo: createModal.memo || '',
          workOrderId: createModal.workOrderId || null,
          teamId: createModal.assignTeamId || null,
        }, token);
        if (createModal.coWorkers && createModal.coWorkers.length > 0 && result?.id) {
          await addOperatorsToProductionJob(result.id, createModal.coWorkers.map(cw => ({
            resourceId: cw,
            start: fullStart,
            end: fullEnd,
          })), token);
        }
      } else if (isAbsenceType && startDate !== endDate) {
        // Full-day absence spanning multiple days: create one job per weekday, skip weekends
        const start = new Date(startDate);
        const end = new Date(endDate);
        const days = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const day = d.getDay(); // 0=Sun, 6=Sat
          if (day !== 0 && day !== 6) {
            // Use local date formatting to avoid timezone shift
            const dy = d.getFullYear();
            const dm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            days.push(`${dy}-${dm}-${dd}`);
          }
        }
        for (const dayStr of days) {
          const fullStart = `${dayStr}T${startTime}:00`;
          const fullEnd = `${dayStr}T${endTime}:00`;
          const result = await createProductionJob({
            resourceId: createModal.resourceId,
            start: fullStart,
            end: fullEnd,
            jobType,
            ordreNr: createModal.ordreNr || '',
            customer: createModal.customer || '',
            memo: createModal.memo || '',
            workOrderId: createModal.workOrderId || null,
          }, token);
          if (createModal.coWorkers && createModal.coWorkers.length > 0 && result?.id) {
            await addOperatorsToProductionJob(result.id, createModal.coWorkers.map(cw => ({
              resourceId: cw,
              start: fullStart,
              end: fullEnd,
            })), token);
          }
        }
      } else {
        // Normal jobs or single-day: create one job as before
        const fullStart = `${startDate}T${startTime}:00`;
        const fullEnd = `${endDate}T${endTime}:00`;
        const result = await createProductionJob({
          resourceId: createModal.resourceId,
          start: fullStart,
          end: fullEnd,
          jobType,
          ordreNr: createModal.ordreNr || '',
          customer: createModal.customer || '',
          memo: createModal.memo || '',
          workOrderId: createModal.workOrderId || null,
          teamId: createModal.assignTeamId || null,
        }, token);
        if (createModal.coWorkers && createModal.coWorkers.length > 0 && result?.id) {
          await addOperatorsToProductionJob(result.id, createModal.coWorkers.map(cw => ({
            resourceId: cw,
            start: fullStart,
            end: fullEnd,
          })), token);
        }
      }

      setCreateModal(null);
      await loadData();
    } catch (err) {
      console.error('Error creating job:', err);
      showAlert('Kunne ikke opprette jobb: ' + (err.message || 'Ukjent feil'));
    }
  };

  // Build edit data from an event object (from events array)
  const buildEditData = (evData) => {
    const ep = evData.extendedProps || {};
    const resourceId = ep.eventResourceId || evData.resourceId;
    // For multi-operator events, preserve the _opN suffix (backend needs it)
    // but strip the _dN daily-split suffix since that's just a display thing.
    let jobId;
    const rawId = String(evData.id);
    if (rawId.includes('_op')) {
      // e.g. "1240_op0_d3" -> "1240_op0", or "1240_op0" stays the same
      jobId = rawId.replace(/_d\d+$/, '');
    } else {
      // e.g. "1240_d3" -> 1240, or "1240" -> 1240
      jobId = ep.parentJobId || rawId.replace(/_d\d+$/, '');
    }
    // Operators are stored one row per person per day-slot, so a multi-day job
    // repeats the same person. Narrow to the clicked day and dedupe by person.
    const eventDay = evData.start ? new Date(evData.start).toDateString() : null;
    let operatorsList = [resourceId];
    if (ep.allOperators) {
      const sameDay = eventDay
        ? ep.allOperators.filter(op => op.start && new Date(op.start).toDateString() === eventDay)
        : [];
      const source = sameDay.length > 0 ? sameDay : ep.allOperators;
      operatorsList = [...new Set(source.map(op => op.resourceId))];
    }
    // Normalize start/end to always be "YYYY-MM-DDTHH:MM:SS"
    const normalizeDateTime = (val, fallbackDate, fallbackTime) => {
      if (!val) return `${fallbackDate}T${fallbackTime}`;
      const str = typeof val === 'string' ? val : new Date(val).toISOString().replace('Z', '');
      if (str.includes('T')) return str.split('.')[0]; // strip milliseconds/timezone
      // Date-only string like "2025-08-27" — attach fallback time
      return `${str}T${fallbackTime}`;
    };
    const startDate = (evData.start || '').includes('T') ? evData.start.split('T')[0] : (evData.start || toLocalDateString(new Date()));
    const normStart = normalizeDateTime(evData.start, startDate, '07:00:00');
    const normEnd = normalizeDateTime(evData.end, normStart.split('T')[0], '15:10:00');

    return {
      jobId,
      resourceId,
      title: evData.title,
      start: normStart,
      end: normEnd,
      jobType: ep.jobType || 'normal',
      ordreNr: ep.ordreNr || '',
      customer: ep.customer || '',
      memo: ep.memo || '',
      completed: ep.completed || false,
      createdBy: ep.createdBy || '',
      operators: operatorsList,
      workOrderId: ep.workOrderId || null,
    };
  };

  // Handle clicking on an existing event — open edit modal
  const handleEventClick = (info) => {
    if (!canCreate) return;
    const ev = info.event;
    const ep = ev.extendedProps || {};
    if (ep.isHoliday) return;

    const evId = ev.id;
    const overlapInfo = overlapMap[evId] || { siblingIds: [] };
    const siblings = overlapInfo.siblingIds || [];

    // Build edit data for the clicked event
    const clickedData = buildEditData({
      id: evId,
      resourceId: ev.getResources?.()[0]?.id || ep.eventResourceId,
      title: ev.title,
      start: ev.startStr,
      end: ev.endStr,
      extendedProps: ep,
    });

    // Build siblings data for all overlapping events
    let siblingData = [];
    if (siblings.length > 1) {
      siblingData = siblings.map(sid => {
        const sibEv = events.find(e => e.id === sid);
        if (!sibEv) return null;
        return buildEditData(sibEv);
      }).filter(Boolean);
    }

    setWorkerSearch('');
    // In day view, jobs are visually distinct — skip the overlap picker
    setEditSiblings(currentViewType === 'resourceTimelineDay' ? [] : siblingData);
    setEditModal(clickedData);

    // Fetch workload for the resource
    setEditWorkload(null);
    if (clickedData.resourceId) {
      console.log('[EditModal] Fetching workload for:', clickedData.resourceId);
      fetchResourceWorkload(clickedData.resourceId, token)
        .then(wl => { console.log('[EditModal] Workload received:', wl); setEditWorkload(wl); })
        .catch((err) => { console.error('[EditModal] Workload fetch failed:', err); setEditWorkload(null); });
    }
  };

  // Update job fields
  const handleUpdateJob = async () => {
    if (!editModal) return;

    // Overlap detection — check if new time range conflicts with existing events on same resource
    const newStart = new Date(editModal.start);
    const newEnd = new Date(editModal.end);
    if (isNaN(newStart.getTime()) || isNaN(newEnd.getTime())) {
      showAlert('Ugyldig dato/tid. Sjekk at alle feltene er fylt ut.');
      return;
    }
    if (newEnd <= newStart) {
      showAlert('Sluttid må være etter starttid.');
      return;
    }

    // Get the real job ID (strip _opN suffix) to know which event IDs belong to this job
    const baseJobId = String(editModal.jobId).replace(/_op\d+$/, '');
    const overlapping = events.filter(ev => {
      // Same resource?
      const evResource = ev.extendedProps?.eventResourceId || ev.resourceId;
      if (String(evResource) !== String(editModal.resourceId)) return false;
      // Skip the job being edited (match by base job ID)
      const evBaseId = String(ev.id).replace(/_op\d+$/, '').replace(/_d\d+$/, '');
      if (evBaseId === baseJobId) return false;
      // Skip holidays
      if (ev.extendedProps?.isHoliday) return false;
      // Check time overlap
      const evStart = new Date(ev.start);
      const evEnd = new Date(ev.end);
      if (isNaN(evStart.getTime()) || isNaN(evEnd.getTime())) return false;
      return newStart < evEnd && newEnd > evStart;
    });

    if (overlapping.length > 0) {
      const conflictList = overlapping.map(ev => {
        const ep = ev.extendedProps || {};
        const st = new Date(ev.start).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' });
        const et = new Date(ev.end).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' });
        const label = ep.ordreNr || ep.customer || ev.title || 'Jobb';
        return `  ${st}–${et}: ${label}`;
      }).join('\n');
      showAlert(`Kan ikke lagre — overlapp med eksisterende jobb(er):\n\n${conflictList}\n\nVelg et annet tidspunkt.`);
      return;
    }

    try {
      await updateProductionJob(editModal.jobId, {
        resourceId: editModal.resourceId,
        start: editModal.start,
        end: editModal.end,
        ordreNr: editModal.ordreNr,
        customer: editModal.customer,
        memo: editModal.memo,
      }, token);
      setEditModal(null);
      await loadData();
    } catch (err) {
      console.error('Error updating job:', err);
      showAlert('Kunne ikke oppdatere jobb: ' + (err.message || 'Ukjent feil'));
    }
  };

  // Delete entire job
  const handleDeleteJob = async () => {
    if (!editModal) return;
    if (!window.confirm('Er du sikker på at du vil slette hele jobben for alle?')) return;
    try {
      await deleteProductionJob(editModal.jobId, token);
      setEditModal(null);
      await loadData();
    } catch (err) {
      console.error('Error deleting job:', err);
      showAlert('Kunne ikke slette: ' + (err.message || 'Ukjent feil'));
    }
  };

  // Remove one operator from the job
  const handleRemoveOperator = async (opResourceId) => {
    if (!editModal) return;
    const opName = resources.find(r => String(r.id) === String(opResourceId))?.title || opResourceId;
    if (!window.confirm(`Fjerne ${opName} fra denne jobben?`)) return;
    try {
      if (editModal.operators.length <= 1) {
        // Last operator — delete the whole job
        await deleteProductionJob(editModal.jobId, token);
        setEditModal(null);
      } else {
        await deleteOperatorFromJob(editModal.jobId, opResourceId, token, editModal.start, editModal.end);
        // Update modal operators list locally
        setEditModal(prev => ({ ...prev, operators: prev.operators.filter(id => String(id) !== String(opResourceId)) }));
      }
      await loadData();
    } catch (err) {
      console.error('Error removing operator:', err);
      showAlert('Kunne ikke fjerne: ' + (err.message || 'Ukjent feil'));
    }
  };

  // Add operator to job
  const handleAddOperator = async (operatorResourceId) => {
    if (!editModal) return;
    try {
      await addOperatorsToProductionJob(editModal.jobId, [{
        resourceId: operatorResourceId,
        start: editModal.start,
        end: editModal.end,
      }], token);
      // Update modal operators list locally
      setEditModal(prev => ({ ...prev, operators: [...prev.operators, operatorResourceId] }));
      await loadData();
    } catch (err) {
      console.error('Error adding operator:', err);
      showAlert('Kunne ikke legge til: ' + (err.message || 'Ukjent feil'));
    }
  };

  if (loading && resources.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#64748b' }}>
        Laster felles kalender...
      </div>
    );
  }

  return (
    <div className="alle-ansatte-widget" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <style>{`
        .alle-ansatte-widget .fc-event {
          border-radius: 2px !important;
          margin: 0 !important;
        }
        .alle-ansatte-widget .fc-event .fc-event-main {
          height: 100% !important;
        }
        /* Timeline view: fill full row height */
        .alle-ansatte-widget .fc-timeline-event {
          height: 100% !important;
          min-height: 100% !important;
          top: 0 !important;
          bottom: 0 !important;
        }
        .alle-ansatte-widget .fc-timeline-lane .fc-timeline-events {
          height: 100% !important;
          padding: 0 !important;
        }
        .alle-ansatte-widget .fc-timeline-event-harness {
          top: 0 !important;
          bottom: 0 !important;
          height: 100% !important;
        }
        /* Timegrid view: fill full row height */
        .alle-ansatte-widget .fc-timegrid-event-harness {
          top: 0 !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
        }
        .alle-ansatte-widget .fc-timegrid-event {
          top: 0 !important;
          bottom: 0 !important;
          height: auto !important;
          min-height: 0 !important;
          position: absolute !important;
          inset: 0 !important;
        }
        .alle-ansatte-widget .fc-timegrid-col-events {
          margin: 0 !important;
        }
        .alle-ansatte-widget .fc-datagrid-cell-cushion {
          padding: 2px 8px !important;
        }
        /* Search match highlight */
        .alle-ansatte-widget .event-highlighted {
          box-shadow: 0 0 0 2px #fbbf24 !important;
          z-index: 5 !important;
        }
        /* Highlighted resource row */
        .alle-ansatte-widget .highlighted-resource-row {
          background-color: #fef9c3 !important;
        }
        .alle-ansatte-widget .highlighted-resource-row td {
          background-color: #fef9c3 !important;
        }
        /* My team row highlight (day view) */
        .alle-ansatte-widget .my-team-row {
          background-color: #fefce8 !important;
        }
        .alle-ansatte-widget .my-team-row td {
          background-color: #fefce8 !important;
        }
        /* Compact FullCalendar header toolbar */
        .alle-ansatte-widget .fc-header-toolbar {
          margin-bottom: 0.25rem !important;
          padding: 0.15rem 0.5rem !important;
          font-size: 0.8rem !important;
        }
        .alle-ansatte-widget .fc-header-toolbar .fc-toolbar-title {
          font-size: 0.85rem !important;
          font-weight: 600 !important;
        }
        .alle-ansatte-widget .fc-header-toolbar .fc-button {
          padding: 0.15rem 0.5rem !important;
          font-size: 0.75rem !important;
        }
        /* Tooltip styles */
        .event-tooltip {
          position: fixed;
          z-index: 99999;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 12px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.15);
          max-width: 320px;
          min-width: 200px;
          pointer-events: none;
        }
        .event-tooltip-row {
          display: flex;
          gap: 4px;
          font-size: 0.8rem;
          margin-bottom: 3px;
        }
        .event-tooltip-label {
          font-weight: 600;
          color: #374151;
          white-space: nowrap;
        }
        .event-tooltip-value {
          color: #64748b;
        }
      `}</style>
      {/* Compact Toolbar + Legend in one row */}
      <div className="aa-toolbar">
        <div className="aa-toolbar-heading">
          <div className="aa-toolbar-icon">👥</div>
          <h3 className="aa-toolbar-title">Felles Kalender</h3>
        </div>
        <input
          type="text"
          placeholder="🔍 Søk ordre/kunde..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="aa-search-input"
        />
        <button onClick={loadData} className="aa-toolbar-btn">
          🔄 Oppdater
        </button>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginLeft: '0.25rem', flexWrap: 'wrap' }}>
          {[
            { color: '#14b8a6', label: 'Normal' },
            { color: '#16a34a', label: '✓ Fullført' },
            { color: '#dc2626', label: '⏰ Overtid' },
            { color: '#ef4444', label: '🤒 Syk' },
            { color: '#f59e0b', label: '🏖️ Ferie' },
            { color: '#3b82f6', label: '💰 Perm.' },
            { color: '#8b5cf6', label: '📅 Perm.' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '0.65rem', color: '#64748b' }}>
              <span style={{
                width: '8px', height: '8px', borderRadius: '2px',
                backgroundColor: item.color, display: 'inline-block', flexShrink: 0
              }} />
              <span>{item.label}</span>
            </div>
          ))}
          <span style={{ color: '#d1d5db', fontSize: '0.65rem' }}>|</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '0.65rem', color: '#64748b' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#14b8a6', display: 'inline-block', flexShrink: 0, outline: '2px solid #d4a017', outlineOffset: '-1px' }} />
            <span>Ordreflytt</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '0.65rem', color: '#64748b' }}>
            <span>👥</span><span>Delt jobb</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '0.65rem', color: '#dc2626', fontWeight: 700 }}>
            <span>2!</span><span style={{ color: '#64748b', fontWeight: 400 }}>Overlapp</span>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {(currentUser?.isAdmin || currentUser?.isTeamLeader || currentUser?.isVacationApprover) && (
          <button onClick={toggleAbsenceSidebar} style={{
            padding: '0.2rem 0.5rem', border: showAbsenceSidebar ? '1px solid #f59e0b' : '1px solid #d1d5db', borderRadius: '8px',
            background: showAbsenceSidebar ? '#fef3c7' : 'white', cursor: 'pointer', fontSize: '0.7rem',
            color: showAbsenceSidebar ? '#92400e' : '#374151', fontWeight: showAbsenceSidebar ? 600 : 400,
            position: 'relative'
          }}>
            📋 Fravær
            {pendingAbsences.length > 0 && (
              <span style={{
                position: 'absolute', top: '-6px', right: '-6px',
                background: '#ef4444', color: 'white', borderRadius: '10px',
                padding: '0 5px', fontSize: '0.6rem', fontWeight: 700, minWidth: '16px', textAlign: 'center'
              }}>{pendingAbsences.length}</span>
            )}
          </button>
        )}
        {currentViewType === 'resourceTimelineDay' && myTeamResourceIds.size > 0 && (
          <button onClick={() => setShowMyTeamOnly(prev => !prev)} style={{
            padding: '0.2rem 0.5rem', border: showMyTeamOnly ? '1px solid #f59e0b' : '1px solid #d1d5db', borderRadius: '8px',
            background: showMyTeamOnly ? '#fef3c7' : 'white', cursor: 'pointer', fontSize: '0.7rem',
            color: showMyTeamOnly ? '#92400e' : '#374151', fontWeight: showMyTeamOnly ? 600 : 400
          }} title="Vis kun mitt team">
            👥 Mitt team
          </button>
        )}
        {canCreate && canUndoLast && (
          <button onClick={handleUndoLast} disabled={reverting} style={{
            padding: '0.2rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px',
            background: reverting ? '#f3f4f6' : 'white', cursor: reverting ? 'not-allowed' : 'pointer', fontSize: '0.7rem', color: '#374151',
            opacity: reverting ? 0.6 : 1
          }} title="Tilbakestill siste kalenderendring">
            {reverting ? '⏳' : '↩️'} Angre
          </button>
        )}
        {canCreate && (
          <button onClick={openLogModal} style={{
            padding: '0.2rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px',
            background: 'white', cursor: 'pointer', fontSize: '0.7rem', color: '#374151'
          }} title="Se logg over alle endringer i kalenderen">
            🔍 Logg
          </button>
        )}
        <span style={{ fontSize: '0.65rem', color: searchTerm.trim() ? '#d97706' : '#94a3b8', fontWeight: searchTerm.trim() ? 600 : 400 }}>
          {searchTerm.trim()
            ? `${filteredResources.length} av ${resources.length} ansatte`
            : `${resources.length} ansatte · ${events.length} jobber`}
        </span>
        {onClose && (
          <button onClick={onClose} className="aa-close-btn">✕ Lukk</button>
        )}
      </div>

      {error && (
        <div style={{ padding: '0.5rem 1rem', backgroundColor: '#fee2e2', color: '#dc2626', fontSize: '0.85rem' }}>
          {error}
          <button onClick={() => setError('')} style={{ marginLeft: '0.5rem', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Calendar + Absence Sidebar */}
      <div style={{ flex: 1, overflow: 'hidden', padding: '0.25rem', display: 'flex', gap: '0.25rem' }}>
        {/* Absence Sidebar */}
        {showAbsenceSidebar && (
          <div style={{
            width: '280px', flexShrink: 0, backgroundColor: '#fffbeb', border: '1px solid #fde68a',
            borderRadius: '8px', padding: '0.75rem', overflowY: 'auto', display: 'flex', flexDirection: 'column'
          }}>
            <div style={{
              fontSize: '0.85rem', fontWeight: 700, color: '#92400e', marginBottom: '0.5rem',
              paddingBottom: '0.4rem', borderBottom: '2px solid #f59e0b',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span>📋 Ventende fravær</span>
              <span style={{
                background: pendingAbsences.length > 0 ? '#ef4444' : '#22c55e', color: 'white',
                borderRadius: '10px', padding: '0.1rem 0.5rem', fontSize: '0.65rem', fontWeight: 700
              }}>{pendingAbsences.length}</span>
            </div>
            {absenceLoading ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#92400e', fontSize: '0.8rem' }}>Laster...</div>
            ) : pendingAbsences.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#92400e', fontSize: '0.8rem' }}>✅ Ingen ventende forespørsler</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {pendingAbsences.map(req => {
                  const typeConfig = {
                    vacation: { label: '🏖️ Ferie', bg: '#fef3c7', color: '#92400e' },
                    sick: { label: '🤒 Syk', bg: '#fee2e2', color: '#dc2626' },
                    free: { label: '💰 Fri', bg: '#e0e7ff', color: '#4338ca' },
                    free_with_pay: { label: '💰 Fri', bg: '#e0e7ff', color: '#4338ca' },
                    free_without_pay: { label: '� Fri', bg: '#e0e7ff', color: '#4338ca' },
                    remote: { label: '🏠 Hjemmekontor', bg: '#dbeafe', color: '#1e40af' },
                    out: { label: '🚗 Ute', bg: '#e0f2fe', color: '#0369a1' },
                  }[req.eventType] || { label: req.eventType, bg: '#f1f5f9', color: '#475569' };
                  const name = req.employee ? `${req.employee.firstName} ${req.employee.lastName}` : 'Ukjent';
                  const startD = req.startDate ? new Date(req.startDate).toLocaleDateString('nb-NO', { day: '2-digit', month: 'short' }) : '';
                  const endD = req.endDate ? new Date(req.endDate).toLocaleDateString('nb-NO', { day: '2-digit', month: 'short' }) : '';
                  return (
                    <div key={req.id} style={{
                      background: 'white', borderRadius: '8px', padding: '0.6rem',
                      border: '1px solid #fde68a', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>👤 {name}</span>
                        <span style={{
                          fontSize: '0.65rem', fontWeight: 600, padding: '0.1rem 0.4rem',
                          borderRadius: '8px', background: typeConfig.bg, color: typeConfig.color
                        }}>{typeConfig.label}</span>
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.2rem' }}>📅 {startD} — {endD}</div>
                      {req.note && <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic', marginBottom: '0.3rem' }}>📝 {req.note}</div>}
                      <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem' }}>
                        <button onClick={() => handleAbsenceAction(req.id, 'approve')} style={{
                          flex: 1, padding: '0.3rem', borderRadius: '8px', border: 'none',
                          background: '#22c55e', color: 'white', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer'
                        }}>✓ Godkjenn</button>
                        <button onClick={() => handleAbsenceAction(req.id, 'reject')} style={{
                          flex: 1, padding: '0.3rem', borderRadius: '8px', border: 'none',
                          background: '#ef4444', color: 'white', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer'
                        }}>✕ Avslå</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <button onClick={loadPendingAbsences} style={{
              marginTop: '0.5rem', padding: '0.3rem', borderRadius: '8px', border: '1px solid #fde68a',
              background: 'white', cursor: 'pointer', fontSize: '0.7rem', color: '#92400e', width: '100%'
            }}>🔄 Oppdater</button>
          </div>
        )}
        <div style={{ flex: 1, height: '100%', overflow: 'hidden' }}>
          <FullCalendar
            ref={calendarRef}
            schedulerLicenseKey={process.env.REACT_APP_FULLCALENDAR_LICENSE_KEY || 'GPL-My-Project-Is-Open-Source'}
            plugins={[resourceTimeGridPlugin, resourceTimelinePlugin, interactionPlugin, listPlugin]}
            initialView="resourceTimelineMonth"
            initialDate={currentDateRef.current}
            datesSet={(dateInfo) => { currentDateRef.current = dateInfo.start; setCurrentViewType(dateInfo.view.type); }}
            viewDidMount={() => { const api = calendarRef.current?.getApi(); if (api) api.today(); }}
            locale={nbLocale}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'resourceTimelineYear,resourceTimelineThreeMonth,resourceTimelineMonth,resourceTimelineWeek,resourceTimelineDay'
            }}
            height="100%"
            resourceAreaWidth="180px"
            views={{
              resourceTimelineDay: {
                type: 'resourceTimeline',
                duration: { days: 1 },
                buttonText: 'Dag',
                slotDuration: '00:30:00',
                slotMinTime: '06:00:00',
                slotMaxTime: '22:00:00',
              },
              resourceTimelineWeek: {
                type: 'resourceTimeline',
                duration: { days: 7 },
                buttonText: 'Uke'
              },
              resourceTimelineMonth: {
                type: 'resourceTimeline',
                duration: { days: 30 },
                buttonText: 'Måned'
              },
              resourceTimelineThreeMonth: {
                type: 'resourceTimeline',
                duration: { months: 3 },
                buttonText: '3 Mnd',
                dateAlignment: 'day'
              },
              resourceTimelineYear: {
                type: 'resourceTimeline',
                duration: { years: 1 },
                buttonText: 'År',
                dateAlignment: 'day'
              }
            }}
            slotLabelContent={(arg) => {
              if (!arg.date) return { html: arg.text };
              // In day view, slots represent hours — show HH:MM
              if (currentViewType === 'resourceTimelineDay') {
                const hh = String(arg.date.getHours()).padStart(2, '0');
                const mm = String(arg.date.getMinutes()).padStart(2, '0');
                return { html: `${hh}:${mm}` };
              }
              const dayNum = arg.date.getDate();
              const dateStr = `${arg.date.getFullYear()}-${String(arg.date.getMonth() + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
              const hName = holidayMap[dateStr];
              if (hName) {
                return { html: `<span style="color:#dc2626;font-weight:700" title="${hName}">${dayNum} 🔴</span>` };
              }
              return { html: `${dayNum}` };
            }}
            resources={filteredResources}
            resourceOrder="order"
            resourceLabelContent={(arg) => (
              <div
                onClick={(e) => { e.stopPropagation(); setHighlightedResource(prev => prev === arg.resource.id ? null : arg.resource.id); }}
                style={{
                  lineHeight: '1', fontSize: '0.8rem', fontWeight: 500,
                  color: highlightedResource === arg.resource.id ? '#92400e' : '#1e293b',
                  background: highlightedResource === arg.resource.id ? '#fef9c3' : 'transparent',
                  borderRadius: '3px', padding: '3px 6px',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer'
                }}
              >
                {arg.resource.title}
              </div>
            )}
            resourceLaneClassNames={(arg) => {
              const classes = [];
              if (arg.resource.id === highlightedResource) classes.push('highlighted-resource-row');
              if (currentViewType === 'resourceTimelineDay' && myTeamResourceIds.has(arg.resource.id)) {
                classes.push('my-team-row');
              }
              return classes;
            }}
            events={allEvents}
            editable={false}
            selectable={true}
            select={handleSelectSlot}
            eventClick={handleEventClick}
            eventClassNames={(arg) => {
              if (!searchTerm) return [];
              const term = searchTerm.toLowerCase();
              const props = arg.event.extendedProps || {};
              const match = (props.ordreNr || '').toLowerCase().includes(term) ||
                            (props.customer || '').toLowerCase().includes(term) ||
                            (props.memo || '').toLowerCase().includes(term);
              return match ? ["event-highlighted"] : [];
            }}
            eventContent={(arg) => {
              const { extendedProps } = arg.event;
              // Skip rendering for background holiday events
              if (extendedProps?.isHoliday) return null;
              const jobType = extendedProps?.jobType || 'normal';

              // Use pre-computed overlap map for O(1) lookup
              const info = overlapMap[arg.event.id] || { index: 1, total: 1, hasOverlap: false };
              const { index: jobIndex, total: totalJobs, hasOverlap } = info;

              // Short label: show job number if multiple, or icon for special types
              const isShared = extendedProps?.isMultiOperator && extendedProps?.totalOperators > 1;
              let shortLabel = '';
              if (totalJobs > 1) {
                shortLabel = jobIndex + '!';
              } else if (extendedProps?.completed) {
                shortLabel = '✓';
              } else if (isShared) {
                shortLabel = '👥';
              } else {
                switch (jobType) {
                  case 'syk': shortLabel = '🤒'; break;
                  case 'ferie': shortLabel = '🏖️'; break;
                  case 'permission_paid': case 'permission_unpaid': shortLabel = '�'; break;
                  case 'reserve': shortLabel = '📌'; break;
                  default: shortLabel = '';
                }
              }

              const resId = arg.event.getResources?.()[0]?.id || extendedProps?.eventResourceId;
              const resName = resources.find(r => String(r.id) === String(resId))?.title || '';

              const isFromOrdreflytt = !!extendedProps?.workOrderId;

              return (
                <div
                  title={`${resName}${extendedProps?.ordreNr ? ' — ' + extendedProps.ordreNr : ''}${extendedProps?.customer ? ' — ' + extendedProps.customer : ''}`}
                  style={{
                    height: '100%', width: '100%',
                    borderRadius: '3px', cursor: 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.75rem',
                    color: hasOverlap ? '#dc2626' : '#000000', fontWeight: 700,
                    overflow: 'hidden', whiteSpace: 'nowrap',
                    ...(isFromOrdreflytt ? { outline: '2px solid #d4a017', outlineOffset: '-2px', borderRadius: '8px' } : {})
                  }}
                  onMouseEnter={(e) => {
                    removeAllEventTooltips();
                    const tooltip = document.createElement('div');
                    tooltip.className = 'event-tooltip';
                    tooltip.id = `tooltip-${arg.event.id}`;

                    // Build tooltip for all overlapping jobs on this slot
                    const buildJobHtml = (ev) => {
                      const ep2 = ev.extendedProps || {};
                      const jt2 = ep2.jobType || 'normal';
                      const done2 = ep2.completed;
                      const typeLabel2 = done2 ? '✓ Fullført' : jt2 === 'syk' ? '🤒 Syk' : jt2 === 'ferie' ? '🏖️ Ferie' : (jt2 === 'permission_paid' || jt2 === 'permission_unpaid') ? '� Fri' : jt2 === 'reserve' ? '📌 Reserve dato' : '📋 Normal jobb';
                      const st2 = ev.start ? new Date(ev.start).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' }) : '—';
                      const et2 = ev.end ? new Date(ev.end).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' }) : '—';
                      let h = `<div style="font-size:0.78rem;font-weight:600;margin-bottom:2px">${typeLabel2}</div>`;
                      h += `<div style="font-size:0.75rem;color:#374151">🕐 ${st2} – ${et2}</div>`;
                      if (ep2.ordreNr) h += `<div style="font-size:0.75rem;color:#374151">📋 ${ep2.ordreNr}</div>`;
                      if (ep2.customer) h += `<div style="font-size:0.75rem;color:#374151">🏢 ${ep2.customer}</div>`;
                      if (ep2.memo) h += `<div style="font-size:0.75rem;color:#64748b">📝 ${ep2.memo}</div>`;
                      if (ep2.createdBy) h += `<div style="font-size:0.72rem;color:#64748b">Opprettet: ${ep2.createdBy}</div>`;
                      return h;
                    };

                    let html = `<div style="font-weight:700;font-size:0.9rem;margin-bottom:4px;color:#111827">👤 ${resName}</div>`;
                    const siblings = info.siblingIds || [];
                    if (siblings.length > 1 && currentViewType !== 'resourceTimelineDay') {
                      // Show all overlapping jobs (only in non-day views)
                      siblings.forEach((sid, idx) => {
                        const sibEv = events.find(ev => ev.id === sid);
                        if (!sibEv) return;
                        if (idx > 0) html += `<div style="border-top:1px solid #e2e8f0;margin:6px 0"></div>`;
                        html += buildJobHtml(sibEv);
                      });
                    } else {
                      html += buildJobHtml({ start: arg.event.start, end: arg.event.end, extendedProps });
                    }

                    tooltip.innerHTML = html;
                    document.body.appendChild(tooltip);
                    const rect = e.currentTarget.getBoundingClientRect();
                    const tooltipRect = tooltip.getBoundingClientRect();
                    let left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
                    let top = rect.bottom + 8;
                    if (left < 10) left = 10;
                    if (left + tooltipRect.width > window.innerWidth - 10) left = window.innerWidth - tooltipRect.width - 10;
                    if (top + tooltipRect.height > window.innerHeight - 10) top = rect.top - tooltipRect.height - 8;
                    if (top < 10) top = 10;
                    tooltip.style.left = `${left}px`;
                    tooltip.style.top = `${top}px`;
                  }}
                  onMouseLeave={() => {
                    removeAllEventTooltips();
                  }}
                >
                  {shortLabel}
                </div>
              );
            }}
            nowIndicator={true}
            eventWillUnmount={removeAllEventTooltips}
            slotEventOverlap={true}
            resourceAreaHeaderContent="Ansatt"
            stickyHeaderDates={true}
            titleFormat={{ weekday: "short", day: "numeric", month: "short", year: "numeric" }}
            businessHours={{
              daysOfWeek: [1, 2, 3, 4, 5],
              startTime: '07:00',
              endTime: '22:00'
            }}
          />
        </div>
      </div>
      {/* Create Job Modal */}
      {createModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.4)'
        }} onClick={() => setCreateModal(null)}>
          <div style={{
            background: 'white', borderRadius: '8px', padding: '1.5rem 2rem',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)', minWidth: '380px', maxWidth: '480px',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
              Ny oppføring
            </h3>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: '#64748b' }}>
              👤 {createModal.resourceName} — {new Date(createModal.start).toLocaleDateString('no-NO', { weekday: 'short', day: 'numeric', month: 'short' })}
            </p>

            {/* Step 1: Job type selection */}
            {(!createModal.step || createModal.step === 1) && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {[
                    { type: 'normal', label: '📋 Ny Jobb', color: '#14b8a6', border: '#0d9488' },
                    { type: 'ferie', label: '🏖️ Ferie', color: '#f59e0b', border: '#d97706' },
                    { type: 'syk', label: '🤒 Syk', color: '#ef4444', border: '#dc2626' },
                    { type: 'permission_paid', label: '💰 Fri', color: '#3b82f6', border: '#4f46e5' },
                    ...(canReserveDates ? [{ type: 'reserve', label: '📌 Reserve dato', color: '#a78bfa', border: '#7c3aed' }] : []),
                  ].map(item => (
                    <button
                      key={item.type}
                      onClick={() => handleSelectJobType(item.type)}
                      style={{
                        padding: '0.6rem 1rem', borderRadius: '8px', border: `2px solid ${item.border}`,
                        background: 'white', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                        color: item.border, textAlign: 'left', transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => { e.target.style.background = item.color; e.target.style.color = 'white'; }}
                      onMouseLeave={(e) => { e.target.style.background = 'white'; e.target.style.color = item.border; }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                <button onClick={() => setCreateModal(null)}
                  style={{ marginTop: '1rem', padding: '0.4rem 1.5rem', borderRadius: '8px', border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', fontSize: '0.8rem', color: '#64748b' }}>
                  Avbryt
                </button>
              </>
            )}

            {/* Step 2: Job details form */}
            {createModal.step === 2 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <button onClick={() => setCreateModal(prev => ({ ...prev, step: 1 }))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#64748b' }}>←</button>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                    {createModal.jobType === 'normal' ? '📋 Ny Jobb' : createModal.jobType === 'ferie' ? '🏖️ Ferie' : createModal.jobType === 'syk' ? '🤒 Syk' : createModal.jobType === 'reserve' ? '📌 Reserve dato' : '💰 Fri'}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                  {/* Work order lookup — only for normal jobs */}
                  {createModal.jobType === 'normal' && (
                    <div>
                      {createModal.linkedOrder ? (
                        <div style={{
                          border: '1px solid #86efac', background: '#f0fdf4', borderRadius: '8px',
                          padding: '0.5rem 0.6rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem'
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#059669' }}>
                              🔗 {createModal.linkedOrder.orderNumber}
                              {createModal.linkedOrder.customer ? ` — ${createModal.linkedOrder.customer}` : ''}
                            </div>
                            {createModal.linkedOrder.description && (
                              <div style={{
                                fontSize: '0.7rem', color: '#475569', marginTop: '0.15rem',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                              }}>
                                {createModal.linkedOrder.description}
                              </div>
                            )}
                            <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '0.15rem' }}>
                              Koblet til arbeidsordre i systemet
                            </div>
                          </div>
                          <button type="button" onClick={handleUnlinkWorkOrder} title="Koble fra ordren"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#059669', padding: 0 }}>
                            ✕
                          </button>
                        </div>
                      ) : (
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                          Søk arbeidsordre
                          <input type="text" value={orderSearch}
                            onChange={(e) => setOrderSearch(e.target.value)}
                            placeholder="Ordrenr., kunde eller beskrivelse…"
                            style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem' }} />
                          {orderSearchLoading && (
                            <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>Søker…</div>
                          )}
                          {!orderSearchLoading && orderSearch.trim().length >= 2 && orderResults.length === 0 && (
                            <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                              Ingen treff — du kan skrive ordrenummer manuelt nedenfor.
                            </div>
                          )}
                          {orderResults.length > 0 && (
                            <div style={{
                              maxHeight: '150px', overflow: 'auto', marginTop: '0.3rem',
                              display: 'flex', flexDirection: 'column', gap: '0.2rem'
                            }}>
                              {orderResults.map(o => (
                                <button key={o.id} type="button"
                                  onClick={() => handleSelectWorkOrder(o)}
                                  style={{
                                    padding: '0.35rem 0.5rem', borderRadius: '8px', border: '1px solid #e2e8f0',
                                    background: 'white', cursor: 'pointer', fontSize: '0.72rem', textAlign: 'left',
                                    color: '#374151', transition: 'all 0.1s'
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#93c5fd'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: 700 }}>{o.orderNumber}</span>
                                    {o.customer ? <span style={{ color: '#475569' }}> · {o.customer}</span> : null}
                                    {o.isStopped ? <span style={{ color: '#dc2626', fontWeight: 600 }}> · ⛔ Stoppet</span> : null}
                                    <span style={{ marginLeft: 'auto', fontSize: '0.6rem', color: '#94a3b8', fontFamily: 'monospace', fontWeight: 600 }}>
                                      ID: {o.id}
                                    </span>
                                  </div>
                                  {o.description && (
                                    <div style={{
                                      color: '#64748b', marginTop: '0.1rem',
                                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                    }}>
                                      {o.description}
                                    </div>
                                  )}
                                  {o.teams && o.teams.length > 0 && (
                                    <div style={{ display: 'flex', gap: '0.2rem', flexWrap: 'wrap', marginTop: '0.15rem' }}>
                                      {o.teams.map(t => (
                                        <span key={t.id} style={{
                                          fontSize: '0.58rem', fontWeight: 600, padding: '0.05rem 0.3rem',
                                          borderRadius: '4px', backgroundColor: '#e0e7ff', color: '#3730a3'
                                        }}>
                                          {t.name}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </label>
                      )}
                    </div>
                  )}

                  {/* Team selector — show teams already assigned to the linked work order */}
                  {createModal.jobType === 'normal' && createModal.linkedOrder && (createModal.linkedOrder.teams || []).length > 0 && (
                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                      Tildel til team
                      <select
                        value={createModal.assignTeamId || ''}
                        onChange={(e) => setCreateModal(prev => ({ ...prev, assignTeamId: e.target.value || null }))}
                        style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem', cursor: 'pointer' }}
                      >
                        <option value="">— Velg team —</option>
                        {createModal.linkedOrder.teams.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </label>
                  )}

                  {/* Order number — only for normal jobs */}
                  {createModal.jobType === 'normal' && (
                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                      Ordrenummer <span style={{ color: '#dc2626' }}>*</span>
                      <input type="text" value={createModal.ordreNr}
                        onChange={(e) => setCreateModal(prev => ({ ...prev, ordreNr: e.target.value }))}
                        placeholder="F.eks. 12345"
                        style={{ width: '100%', padding: '0.4rem 0.5rem', border: `1px solid ${(createModal.ordreNr || '').trim() ? '#d1d5db' : '#fca5a5'}`, borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem' }} />
                    </label>
                  )}

                  {/* Customer — only for normal jobs */}
                  {createModal.jobType === 'normal' && (
                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                      Kunde <span style={{ color: '#dc2626' }}>*</span>
                      <input type="text" value={createModal.customer}
                        onChange={(e) => setCreateModal(prev => ({ ...prev, customer: e.target.value }))}
                        placeholder="Kundenavn"
                        style={{ width: '100%', padding: '0.4rem 0.5rem', border: `1px solid ${(createModal.customer || '').trim() ? '#d1d5db' : '#fca5a5'}`, borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem' }} />
                    </label>
                  )}

                  {/* Day-part toggle — only for absence types (ferie/syk/fri) */}
                  {createModal.jobType !== 'normal' && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={() => setCreateModal(prev => ({ ...prev, partialDay: false }))}
                        style={{
                          flex: 1, padding: '0.45rem', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 600,
                          cursor: 'pointer', border: `1px solid ${createModal.partialDay ? '#e2e8f0' : '#3b82f6'}`,
                          background: createModal.partialDay ? '#f8fafc' : '#3b82f6',
                          color: createModal.partialDay ? '#64748b' : 'white',
                        }}
                      >
                        📅 Hel dag
                      </button>
                      <button
                        type="button"
                        onClick={() => setCreateModal(prev => ({ ...prev, partialDay: true, end: prev.start }))}
                        style={{
                          flex: 1, padding: '0.45rem', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 600,
                          cursor: 'pointer', border: `1px solid ${createModal.partialDay ? '#f59e0b' : '#e2e8f0'}`,
                          background: createModal.partialDay ? '#f59e0b' : '#f8fafc',
                          color: createModal.partialDay ? 'white' : '#64748b',
                        }}
                      >
                        🕐 Deler av dagen
                      </button>
                    </div>
                  )}

                  {/* Date range */}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <label style={{ flex: 1, fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                      {createModal.partialDay ? 'Dato' : 'Fra dato'}
                      <input type="date" value={createModal.start?.split('T')[0] || ''}
                        onChange={(e) => {
                          const newDate = e.target.value;
                          const todayStr = toLocalDateString(new Date());
                          const isToday = newDate === todayStr;
                          const now = new Date();
                          const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                          setCreateModal(prev => ({
                            ...prev,
                            start: newDate,
                            ...(prev.partialDay ? { end: newDate } : {}),
                            // Only force current time for partial-day events on today (no back-registering).
                            // Multi-day absences always start at 07:00.
                            ...(isToday && prev.partialDay && (prev.startTime || '07:00') < currentTimeStr ? { startTime: currentTimeStr } : {}),
                          }));
                        }}
                        style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem' }} />
                    </label>
                    {!createModal.partialDay && (
                      <label style={{ flex: 1, fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                        Til dato
                        <input type="date" value={createModal.end?.split('T')[0] || ''}
                          onChange={(e) => setCreateModal(prev => ({ ...prev, end: e.target.value }))}
                          style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem' }} />
                      </label>
                    )}
                  </div>

                  {/* Time — for normal jobs, or for absence types when "Deler av dagen" is selected */}
                  {(createModal.jobType === 'normal' || createModal.partialDay) && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <label style={{ flex: 1, fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                        Start kl.
                        <input type="time" value={createModal.startTime || '07:00'}
                          min={(() => {
                            const todayStr = toLocalDateString(new Date());
                            const selectedDate = createModal.start?.split('T')[0] || '';
                            if (selectedDate === todayStr) {
                              // 5-minute grace period so "now" isn't blocked by the time the user submits
                              const grace = new Date(Date.now() - 5 * 60 * 1000);
                              return `${String(grace.getHours()).padStart(2, '0')}:${String(grace.getMinutes()).padStart(2, '0')}`;
                            }
                            return undefined;
                          })()}
                          onChange={(e) => setCreateModal(prev => ({ ...prev, startTime: e.target.value }))}
                          style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem' }} />
                      </label>
                      <label style={{ flex: 1, fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                        Slutt kl.
                        <input type="time" value={createModal.endTime || '15:10'}
                          onChange={(e) => setCreateModal(prev => ({ ...prev, endTime: e.target.value }))}
                          style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem' }} />
                      </label>
                    </div>
                  )}

                  {/* Memo — only for normal jobs */}
                  {createModal.jobType === 'normal' && (
                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                      Notat <span style={{ color: '#dc2626' }}>*</span>
                      <textarea value={createModal.memo}
                        onChange={(e) => setCreateModal(prev => ({ ...prev, memo: e.target.value }))}
                        rows={2} placeholder="Beskriv jobben"
                        style={{ width: '100%', padding: '0.4rem 0.5rem', border: `1px solid ${(createModal.memo || '').trim() ? '#d1d5db' : '#fca5a5'}`, borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem', resize: 'vertical' }} />
                    </label>
                  )}

                  {/* Optional note — for reserve dates */}
                  {createModal.jobType === 'reserve' && (
                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                      Notat (valgfritt)
                      <textarea value={createModal.memo}
                        onChange={(e) => setCreateModal(prev => ({ ...prev, memo: e.target.value }))}
                        rows={2} placeholder="Valgfritt notat til reservasjonen"
                        style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem', resize: 'vertical' }} />
                    </label>
                  )}

                  {/* Co-workers — only for normal jobs */}
                  {createModal.jobType === 'normal' && (
                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '0.75rem' }}>
                      <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', margin: '0 0 0.4rem 0' }}>👥 Andre ansatte på denne jobben</p>
                      {createModal.coWorkers && createModal.coWorkers.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.5rem' }}>
                          {createModal.coWorkers.map(cwId => {
                            const r = resources.find(res => String(res.id) === String(cwId));
                            return (
                              <span key={cwId} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                                padding: '0.2rem 0.5rem', borderRadius: '8px', fontSize: '0.72rem',
                                background: '#dbeafe', color: '#1e40af', fontWeight: 500
                              }}>
                                {r?.title || cwId}
                                <button onClick={() => setCreateModal(prev => ({ ...prev, coWorkers: prev.coWorkers.filter(id => id !== cwId) }))}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem', color: '#1e40af', padding: 0 }}>✕</button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <input type="text" value={workerSearch}
                        onChange={(e) => setWorkerSearch(e.target.value)}
                        placeholder="Søk etter ansatt..."
                        style={{ width: '100%', padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.75rem', marginBottom: '0.3rem' }} />
                      <div style={{ maxHeight: '120px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        {resources
                          .filter(r => String(r.id) !== String(createModal.resourceId) && !(createModal.coWorkers || []).includes(r.id))
                          .filter(r => !workerSearch || (r.title || '').toLowerCase().includes(workerSearch.toLowerCase()))
                          .map(r => (
                            <button key={r.id}
                              onClick={() => { setCreateModal(prev => ({ ...prev, coWorkers: [...(prev.coWorkers || []), r.id] })); setWorkerSearch(''); }}
                              style={{
                                padding: '0.25rem 0.5rem', borderRadius: '8px', border: '1px solid #e2e8f0',
                                background: 'white', cursor: 'pointer', fontSize: '0.72rem', textAlign: 'left',
                                color: '#374151', transition: 'all 0.1s'
                              }}
                              onMouseEnter={(e) => { e.target.style.background = '#eff6ff'; e.target.style.borderColor = '#93c5fd'; }}
                              onMouseLeave={(e) => { e.target.style.background = 'white'; e.target.style.borderColor = '#e2e8f0'; }}
                            >
                              + {r.title}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button onClick={() => setCreateModal(null)}
                    style={{ padding: '0.45rem 1rem', borderRadius: '8px', border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', fontSize: '0.8rem', color: '#64748b' }}>
                    Avbryt
                  </button>
                  <button onClick={handleCreateJob}
                    disabled={!isCreateFormValid(createModal)}
                    title={!isCreateFormValid(createModal) ? 'Ordrenummer, Kunde og Notat må fylles ut' : undefined}
                    style={{
                      padding: '0.45rem 1.2rem', borderRadius: '8px', border: 'none',
                      background: isCreateFormValid(createModal) ? '#3b82f6' : '#cbd5e1',
                      cursor: isCreateFormValid(createModal) ? 'pointer' : 'not-allowed',
                      fontSize: '0.8rem', fontWeight: 600, color: 'white',
                    }}>
                    Opprett
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Edit Job Modal */}
      {editModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.4)'
        }} onClick={() => setEditModal(null)}>
          <div style={{
            background: 'white', borderRadius: '8px', padding: '1.5rem 2rem',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)', minWidth: '400px', maxWidth: '500px',
            maxHeight: '90vh', overflowY: 'auto',
          }} onClick={(e) => e.stopPropagation()}>
            {/* Overlap picker — show all overlapping jobs as switchable cards */}
            {editSiblings.length > 1 && (
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', margin: '0 0 0.4rem 0' }}>
                  ⚠️ {editSiblings.length} jobber overlapper — velg hvilken du vil redigere:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  {editSiblings.map((sib, idx) => {
                    const isActive = sib.jobId === editModal.jobId;
                    const jt = sib.jobType || 'normal';
                    const typeIcon = jt === 'syk' ? '🤒' : jt === 'ferie' ? '🏖️' : (jt === 'permission_paid' || jt === 'permission_unpaid') ? '�' : jt === 'reserve' ? '📌' : '📋';
                    return (
                      <div key={sib.jobId + '_' + idx}
                        onClick={() => { if (!isActive) { setWorkerSearch(''); setEditModal(sib); } }}
                        style={{
                          padding: '0.4rem 0.6rem', borderRadius: '8px', cursor: isActive ? 'default' : 'pointer',
                          border: isActive ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                          background: isActive ? '#eff6ff' : 'white',
                          transition: 'all 0.15s',
                        }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.78rem', fontWeight: isActive ? 700 : 500, color: isActive ? '#1e40af' : '#374151' }}>
                            {typeIcon} {sib.ordreNr || sib.customer || sib.title || 'Jobb ' + (idx + 1)}
                          </span>
                          {isActive && <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#3b82f6' }}>REDIGERER ✏️</span>}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                          🕐 {sib.start ? new Date(sib.start).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' }) : '—'}
                          {' – '}
                          {sib.end ? new Date(sib.end).toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' }) : '—'}
                          {sib.customer ? ` · 🏢 ${sib.customer}` : ''}
                          {sib.createdBy ? ` · ${sib.createdBy}` : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
              ✏️ Rediger jobb
            </h3>
            {/* Editable date & time */}
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#374151' }}>
                Dato
                <input type="date"
                  value={(() => { const s = editModal.start || ''; return s.includes('T') ? s.split('T')[0] : s.substring(0, 10); })()}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setEditModal(prev => {
                      const startTime = (prev.start || '').includes('T') ? prev.start.split('T')[1] : '07:00:00';
                      const endTime = (prev.end || '').includes('T') ? prev.end.split('T')[1] : '15:10:00';
                      return { ...prev, start: `${newDate}T${startTime}`, end: `${newDate}T${endTime}` };
                    });
                  }}
                  style={{ display: 'block', padding: '0.3rem 0.4rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.78rem', marginTop: '0.1rem' }}
                />
              </label>
              <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#374151' }}>
                Fra
                <input type="time"
                  value={(() => { const s = editModal.start || ''; return s.includes('T') ? s.split('T')[1]?.substring(0, 5) : '07:00'; })()}
                  onChange={(e) => {
                    setEditModal(prev => {
                      const date = (prev.start || '').includes('T') ? prev.start.split('T')[0] : toLocalDateString(new Date());
                      return { ...prev, start: `${date}T${e.target.value}:00` };
                    });
                  }}
                  style={{ display: 'block', padding: '0.3rem 0.4rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.78rem', marginTop: '0.1rem' }}
                />
              </label>
              <label style={{ fontSize: '0.72rem', fontWeight: 600, color: '#374151' }}>
                Til
                <input type="time"
                  value={(() => { const s = editModal.end || ''; return s.includes('T') ? s.split('T')[1]?.substring(0, 5) : '15:10'; })()}
                  onChange={(e) => {
                    setEditModal(prev => {
                      // Use the end date, or fall back to start date
                      let date = (prev.end || '').includes('T') ? prev.end.split('T')[0] : '';
                      if (!date) date = (prev.start || '').includes('T') ? prev.start.split('T')[0] : toLocalDateString(new Date());
                      return { ...prev, end: `${date}T${e.target.value}:00` };
                    });
                  }}
                  style={{ display: 'block', padding: '0.3rem 0.4rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.78rem', marginTop: '0.1rem' }}
                />
              </label>
            </div>
            {editModal.createdBy && (
              <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.72rem', color: '#94a3b8' }}>Opprettet av: {editModal.createdBy}</p>
            )}

            {/* Resource workload panel */}
            {editWorkload && (
              <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.6rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editWorkload.jobs?.length > 0 ? '0.4rem' : '0' }}>
                  <span style={{ fontWeight: 600, color: '#334155' }}>
                    Uke {(() => { const d = new Date(editWorkload.weekStart); const jan4 = new Date(d.getFullYear(), 0, 4); return Math.ceil(((d - jan4) / 86400000 + jan4.getDay() + 1) / 7); })()}
                  </span>
                  <span style={{
                    fontWeight: 700, padding: '0.1rem 0.4rem', borderRadius: '6px', fontSize: '0.72rem',
                    backgroundColor: editWorkload.totalHours >= editWorkload.capacityHours ? '#fee2e2' : editWorkload.totalHours >= editWorkload.capacityHours * 0.8 ? '#fef3c7' : '#dcfce7',
                    color: editWorkload.totalHours >= editWorkload.capacityHours ? '#991b1b' : editWorkload.totalHours >= editWorkload.capacityHours * 0.8 ? '#92400e' : '#059669'
                  }}>
                    {editWorkload.totalHours}t / {editWorkload.capacityHours}t
                  </span>
                </div>
                {editWorkload.jobs?.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', maxHeight: '120px', overflowY: 'auto' }}>
                    {editWorkload.jobs.map((job, idx) => {
                      const start = new Date(job.start);
                      const dayName = start.toLocaleDateString('no-NO', { weekday: 'short' });
                      const timeStr = start.toLocaleTimeString('no-NO', { hour: '2-digit', minute: '2-digit' });
                      return (
                        <div key={idx} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '0.2rem 0.4rem', backgroundColor: job.completed ? '#f0fdf4' : 'white',
                          borderRadius: '6px', border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 500, minWidth: '50px' }}>
                              {dayName} {timeStr}
                            </span>
                            <span style={{ fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.72rem' }}>
                              {job.ordreNr || job.customer || 'Jobb'}
                            </span>
                          </div>
                          <span style={{ fontWeight: 600, color: '#3b82f6', marginLeft: '0.4rem', whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
                            {job.hours}t {job.completed ? '✅' : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {(!editWorkload.jobs || editWorkload.jobs.length === 0) && (
                  <div style={{ color: '#059669', fontSize: '0.72rem' }}>✅ Ingen andre jobber denne uken</div>
                )}
              </div>
            )}

            {/* Job type badge */}
            <div style={{ marginBottom: '0.75rem' }}>
              <span style={{
                padding: '0.2rem 0.6rem', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600,
                background: editModal.jobType === 'syk' ? '#fee2e2' : editModal.jobType === 'ferie' ? '#fef3c7' : (editModal.jobType === 'permission_paid' || editModal.jobType === 'permission_unpaid') ? '#e0e7ff' : editModal.jobType === 'reserve' ? '#ede9fe' : '#ccfbf1',
                color: editModal.jobType === 'syk' ? '#dc2626' : editModal.jobType === 'ferie' ? '#d97706' : (editModal.jobType === 'permission_paid' || editModal.jobType === 'permission_unpaid') ? '#4f46e5' : editModal.jobType === 'reserve' ? '#7c3aed' : '#0d9488',
              }}>
                {editModal.jobType === 'syk' ? '🤒 Syk' : editModal.jobType === 'ferie' ? '🏖️ Ferie' : (editModal.jobType === 'permission_paid' || editModal.jobType === 'permission_unpaid') ? '� Fri' : editModal.jobType === 'reserve' ? '📌 Reserve dato' : '📋 Normal jobb'}
              </span>
              {editModal.completed && <span style={{ marginLeft: '0.5rem', padding: '0.2rem 0.6rem', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, background: '#dcfce7', color: '#16a34a' }}>✓ Fullført</span>}
              {editModal.workOrderId && onOpenInOrdersFlow && (
                <button onClick={() => { setEditModal(null); onOpenInOrdersFlow(editModal.workOrderId); }}
                  style={{
                    marginLeft: '0.5rem', padding: '0.2rem 0.6rem', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600,
                    background: '#fef9c3', color: '#92400e', border: '2px solid #d4a017', cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={(e) => { e.target.style.background = '#fde68a'; }}
                  onMouseLeave={(e) => { e.target.style.background = '#fef9c3'; }}
                >
                  📋 Åpne i Ordreflytt
                </button>
              )}
            </div>

            {/* Current operators — shown as removable chips */}
            <div style={{ marginBottom: '0.75rem' }}>
              <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', margin: '0 0 0.4rem 0' }}>
                👥 Ansatte på jobben
                {editModal.start && (
                  <span style={{ fontWeight: 500, color: '#64748b' }}>
                    {' '}— {new Date(editModal.start).toLocaleDateString('no-NO', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </span>
                )}
              </p>
              {/* Show machine if the job is assigned to a machine */}
              {editModal.resourceId && String(editModal.resourceId).startsWith('machine_') && (() => {
                const machineRes = resources.find(res => String(res.id) === String(editModal.resourceId));
                return machineRes ? (
                  <div style={{ marginBottom: '0.4rem' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.25rem 0.6rem', borderRadius: '14px', fontSize: '0.75rem',
                      background: '#fef3c7', color: '#92400e', fontWeight: 500
                    }}>
                      🔧 {machineRes.title}
                    </span>
                  </div>
                ) : null;
              })()}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                {(editModal.operators || []).map(opId => {
                  const r = resources.find(res => String(res.id) === String(opId));
                  return (
                    <span key={opId} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.25rem 0.6rem', borderRadius: '14px', fontSize: '0.75rem',
                      background: '#dbeafe', color: '#1e40af', fontWeight: 500
                    }}>
                      👤 {r?.title || opId}
                      <button onClick={() => handleRemoveOperator(opId)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#dc2626', padding: 0, marginLeft: '2px' }}>✕</button>
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Editable fields — only for normal jobs */}
            {editModal.jobType === 'normal' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                  Ordrenr.
                  <input type="text" value={editModal.ordreNr} onChange={(e) => setEditModal({ ...editModal, ordreNr: e.target.value })}
                    style={{ width: '100%', padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem' }} />
                </label>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                  Kunde
                  <input type="text" value={editModal.customer} onChange={(e) => setEditModal({ ...editModal, customer: e.target.value })}
                    style={{ width: '100%', padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem' }} />
                </label>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                  Notat
                  <textarea rows={2} value={editModal.memo} onChange={(e) => setEditModal({ ...editModal, memo: e.target.value })}
                    style={{ width: '100%', padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.8rem', marginTop: '0.15rem', resize: 'vertical' }} />
                </label>
              </div>
            )}

            {/* Add new operator — only for normal jobs */}
            {editModal.jobType === 'normal' && (
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '0.75rem', marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', margin: '0 0 0.4rem 0' }}>+ Legg til person</p>
                <input type="text" value={workerSearch}
                  onChange={(e) => setWorkerSearch(e.target.value)}
                  placeholder="Søk etter ansatt..."
                  style={{ width: '100%', padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.75rem', marginBottom: '0.3rem' }} />
                <div style={{ maxHeight: '120px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                  {resources
                    .filter(r => !(editModal.operators || []).some(opId => String(opId) === String(r.id)))
                    .filter(r => !workerSearch || (r.title || '').toLowerCase().includes(workerSearch.toLowerCase()))
                    .map(r => (
                      <button key={r.id} onClick={() => { handleAddOperator(r.id); setWorkerSearch(''); }}
                        style={{
                          padding: '0.25rem 0.5rem', borderRadius: '8px', border: '1px solid #e2e8f0',
                          background: 'white', cursor: 'pointer', fontSize: '0.72rem', textAlign: 'left',
                          color: '#374151', transition: 'all 0.1s'
                        }}
                        onMouseEnter={(e) => { e.target.style.background = '#eff6ff'; e.target.style.borderColor = '#93c5fd'; }}
                        onMouseLeave={(e) => { e.target.style.background = 'white'; e.target.style.borderColor = '#e2e8f0'; }}
                      >
                        + {r.title}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleDeleteJob}
                  style={{
                    padding: '0.45rem 1rem', borderRadius: '8px', border: '1px solid #fecaca',
                    background: '#fef2f2', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: '#dc2626'
                  }}>
                  🗑️ Slett jobb
                </button>
                <button onClick={() => {
                  const resourceId = editModal.resourceId;
                  const resourceName = resources.find(r => String(r.id) === String(resourceId))?.title || 'Ansatt';
                  const dateStr = editModal.start ? toLocalDateString(new Date(editModal.start)) : toLocalDateString(new Date());
                  setEditModal(null);
                  setCreateModal({ resourceId, resourceName, start: dateStr, end: dateStr });
                }}
                  style={{
                    padding: '0.45rem 1rem', borderRadius: '8px', border: '1px solid #bbf7d0',
                    background: '#f0fdf4', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: '#16a34a'
                  }}>
                  ➕ Ny jobb her
                </button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setEditModal(null)}
                  style={{
                    padding: '0.45rem 1rem', borderRadius: '8px', border: '1px solid #d1d5db',
                    background: '#f9fafb', cursor: 'pointer', fontSize: '0.8rem', color: '#64748b'
                  }}>
                  Avbryt
                </button>
                <button onClick={handleUpdateJob}
                  style={{
                    padding: '0.45rem 1rem', borderRadius: '8px', border: 'none',
                    background: '#3b82f6', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: 'white'
                  }}>
                  💾 Lagre
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Audit Log Modal */}
      {showLogModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.4)'
        }} onClick={() => setShowLogModal(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white', borderRadius: '10px', width: '600px', maxWidth: '92vw',
            maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{
              padding: '0.9rem 1.1rem', borderBottom: '1px solid #e2e8f0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', color: '#111827' }}>🔍 Kalenderlogg</h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button onClick={loadAuditLogs} style={{
                  padding: '0.2rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '8px',
                  background: 'white', cursor: 'pointer', fontSize: '0.7rem', color: '#374151'
                }}>🔄</button>
                <button onClick={() => setShowLogModal(false)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#64748b'
                }}>✕</button>
              </div>
            </div>
            <div style={{ padding: '0.75rem 1.1rem', overflowY: 'auto', flex: 1 }}>
              {auditLogLoading && (
                <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem', padding: '1rem' }}>Laster logg...</div>
              )}
              {auditLogError && (
                <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem', borderRadius: '8px', fontSize: '0.8rem' }}>
                  {auditLogError}
                </div>
              )}
              {!auditLogLoading && !auditLogError && auditLogs.length === 0 && (
                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem', padding: '1rem' }}>Ingen hendelser funnet</div>
              )}
              {!auditLogLoading && !auditLogError && auditLogs.map(log => {
                const ACTION_STYLE = {
                  CREATE: { bg: '#dcfce7', text: '#059669', label: 'Opprettet' },
                  UPDATE: { bg: '#dbeafe', text: '#1e40af', label: 'Oppdatert' },
                  DELETE: { bg: '#fee2e2', text: '#991b1b', label: 'Slettet' },
                }[log.action] || { bg: '#f1f5f9', text: '#475569', label: log.action };
                const when = log.created_at ? new Date(log.created_at).toLocaleString('nb-NO') : '';
                return (
                  <div key={log.id} style={{
                    display: 'flex', gap: '0.6rem', padding: '0.55rem 0', borderBottom: '1px solid #f1f5f9'
                  }}>
                    <span style={{
                      background: ACTION_STYLE.bg, color: ACTION_STYLE.text, borderRadius: '5px',
                      padding: '0.1rem 0.45rem', fontSize: '0.68rem', fontWeight: 700, alignSelf: 'flex-start', flexShrink: 0
                    }}>{ACTION_STYLE.label}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.82rem', color: '#111827' }}>{log.changes_summary || '—'}</div>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.1rem' }}>
                        {log.performed_by_user_name || log.user_name || 'Ukjent bruker'} · {when}
                      </div>
                    </div>
                    {canCreate && (currentUser?.isAdmin || log.user_id === currentUser?.id) && (
                      <button
                        onClick={() => handleRevert(log.id, log.changes_summary)}
                        disabled={reverting}
                        title="Tilbakestill denne endringen"
                        style={{
                          padding: '0.15rem 0.4rem', border: '1px solid #e2e8f0', borderRadius: '8px',
                          background: reverting ? '#f3f4f6' : 'white', cursor: reverting ? 'not-allowed' : 'pointer',
                          fontSize: '0.65rem', color: '#64748b', fontWeight: 600, flexShrink: 0, alignSelf: 'flex-start',
                          opacity: reverting ? 0.6 : 1
                        }}
                      >
                        ↩️ Tilbakestill
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Custom alert modal — replaces native alert() */}
      {systemAlert && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 100000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setSystemAlert(null)}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '1.5rem', minWidth: '320px', maxWidth: '480px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)', position: 'relative'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#374151', marginBottom: '0.75rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem' }}>
              System sier
            </div>
            <div style={{ fontSize: '0.82rem', color: '#1f2937', whiteSpace: 'pre-wrap', lineHeight: 1.5, marginBottom: '1rem' }}>
              {systemAlert.message}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setSystemAlert(null)} style={{
                padding: '0.35rem 1.2rem', borderRadius: '8px', border: '1px solid #d1d5db',
                background: '#f3f4f6', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: '#374151'
              }}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlleAnsatteWidget;
